import React, {useState} from 'react'
import {
    browserLocalPersistence,
    createUserWithEmailAndPassword,
    setPersistence,
    signOut,
    updateProfile
} from 'firebase/auth';
import {auth, db} from '../firebase-config';
import {doc, setDoc} from 'firebase/firestore';
import {useNavigate} from "react-router-dom";

function Signup() {
    const [username, setUsername] = useState('')
    const [email, setEmail] = useState('')
    const [password, setPassword] = useState('')
    const [confirmPassword, setConfirmPassword] = useState('')
    const [signupError, setSignupError] = useState('')
    const [signupSuccess, setSignupSuccess] = useState('')
    const [isSubmitting, setIsSubmitting] = useState(false)
    const navigate = useNavigate()

    const handleGoToLogin = () => {
        signOut(auth).catch((err) => {
            console.error("Sign out after signup failed:", err);
        })
        navigate("/signin")
    }

    const handleSignup = async (event) => {
        event.preventDefault()
        setSignupError('')
        setSignupSuccess('')

        if (password !== confirmPassword) {
            setSignupError('Passwords do not match.')
            return
        }

        setIsSubmitting(true)

        try {
            await setPersistence(auth, browserLocalPersistence)
            const {user} = await createUserWithEmailAndPassword(auth, email, password)

            setSignupSuccess('Your account has been created successfully. You can now log in.')

            Promise.allSettled([
                updateProfile(user, {displayName: username}),
                setDoc(doc(db, 'users', user.uid), {
                    uid: user.uid,
                    email: user.email,
                    displayName: username,
                    createdAt: new Date()
                })
            ]).then((results) => {
                results.forEach((result) => {
                    if (result.status === 'rejected') {
                        console.error("Post-signup profile setup failed:", result.reason);
                    }
                })
            })
        } catch (err) {
            const authErrorMessages = {
                "auth/email-already-in-use": "This email is already in use.",
                "auth/invalid-email": "Please enter a valid email address.",
                "auth/weak-password": "Password should be at least 6 characters."
            };
            const message = authErrorMessages[err.code] || "Sign up failed. Please check your details and try again.";

            setSignupError(message)
            console.error("Signup error:", err.code, err.message, err);
        } finally {
            setIsSubmitting(false)
        }

    }

    return (
        <div className="min-h-screen bg-slate-50 px-4 py-10 sm:py-16">
            <div className="mx-auto w-full max-w-md rounded-2xl border border-slate-200 bg-white p-6 shadow-sm sm:p-8">
                <h2 className="text-center text-3xl font-semibold tracking-tight text-slate-900">
                    Compitino
                </h2>
                <p className="mt-2 text-center text-sm text-slate-500">Create your account 💪</p>

                {signupSuccess ? (
                    <div className="mt-8 space-y-5">
                        <p className="rounded-lg border border-green-200 bg-green-50 px-3 py-3 text-sm text-green-700" role="status">
                            {signupSuccess}
                        </p>
                        <button
                            type="button"
                            className="h-11 w-full rounded-xl bg-indigo-600 font-medium text-white transition hover:bg-indigo-700"
                            onClick={handleGoToLogin}
                        >
                            Go to Login
                        </button>
                    </div>
                ) : (
                <form onSubmit={handleSignup} className="mt-8 space-y-5">
                    <div className="space-y-2">
                        <label className="text-sm font-medium text-slate-600">Username</label>
                        <input
                            type="text"
                            onChange={(e) => {
                                setUsername(e.target.value)
                                setSignupError('')
                                setSignupSuccess('')
                            }}
                            required
                            className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-slate-800 outline-none transition focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100"
                        />
                    </div>

                    <div className="space-y-2">
                        <label className="text-sm font-medium text-slate-600">Email</label>
                        <input
                            type="email"
                            onChange={(e) => {
                                setEmail(e.target.value)
                                setSignupError('')
                                setSignupSuccess('')
                            }}
                            required
                            className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-slate-800 outline-none transition focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100"
                        />
                    </div>

                    <div className="space-y-2">
                        <label className="text-sm font-medium text-slate-600">Password</label>
                        <input
                            type="password"
                            onChange={(e) => {
                                setPassword(e.target.value)
                                setSignupError('')
                                setSignupSuccess('')
                            }}
                            required
                            className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-slate-800 outline-none transition focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100"
                        />
                    </div>

                    <div className="space-y-2">
                        <label className="text-sm font-medium text-slate-600">Confirm Password</label>
                        <input
                            type="password"
                            onChange={(e) => {
                                setConfirmPassword(e.target.value)
                                setSignupError('')
                                setSignupSuccess('')
                            }}
                            required
                            className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-slate-800 outline-none transition focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100"
                        />
                    </div>

                    {signupError && (
                        <p className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700" role="alert">
                            {signupError}
                        </p>
                    )}
                    <button
                        type="submit"
                        disabled={isSubmitting}
                        className="h-11 w-full rounded-xl bg-indigo-600 font-medium text-white transition hover:bg-indigo-700 disabled:cursor-not-allowed disabled:bg-indigo-400"
                    >
                        {isSubmitting ? 'Creating Account...' : 'Create Account'}
                    </button>
                </form>
                )}

                {!signupSuccess && (
                    <>
                        <div className="my-5 flex items-center gap-3 text-xs text-slate-400">
                            <span className="h-px flex-1 bg-slate-200"/>
                            <span>or</span>
                            <span className="h-px flex-1 bg-slate-200"/>
                        </div>

                        <button
                            type="button"
                            className="h-11 w-full rounded-xl border border-slate-200 bg-white font-medium text-slate-700 transition hover:bg-slate-50"
                            onClick={() => navigate("/signin")}
                        >
                            Login
                        </button>
                    </>
                )}

                <p className="mt-5 text-center text-xs text-slate-400">
                    You can update your profile details later in settings.
                </p>
            </div>
        </div>
    )
}

export default Signup
